/**
 * <AIHelpsHereCallout /> — the AI-tinted "what AI does in this
 * step" banner that lives at the top of every M27 step page (lock #5
 * — AI prominence requirements).
 *
 * Per DESIGN_SYSTEM.md §6: brand-teal sparkle ✦, AI-tinted
 * background (`bg-ai-bg`), AI-deep text for AA contrast.
 */

import { Sparkles } from "lucide-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

export interface AIHelpsHereCalloutProps {
  /** Plain-text headline — usually starts with "I'll …" or "I can …" */
  message: string;
  /** Optional secondary line elaborating the action. */
  detail?: string;
  /** Optional CTA — typically a "Show me" or "Generate" button. */
  action?: ReactNode;
}

export function AIHelpsHereCallout({
  message,
  detail,
  action,
}: AIHelpsHereCalloutProps) {
  const { t } = useTranslation();
  return (
    <aside
      role="note"
      aria-label={t("app.ai_helps_aria")}
      className="flex items-start gap-3 rounded-xl border border-ai-subtle bg-ai-bg px-4 py-3"
    >
      <Sparkles
        size={16}
        className="mt-0.5 shrink-0 text-brand-teal"
        aria-hidden
      />
      <div className="flex-1">
        <p className="text-sm font-medium text-ai-deep">{message}</p>
        {detail && (
          <p className="mt-1 text-xs text-text-secondary">{detail}</p>
        )}
      </div>
      {action && <div className="ms-auto shrink-0">{action}</div>}
    </aside>
  );
}
